import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class BancoPlazaService {
  private baseUrl = environment.BASE_LA;

  constructor(private http: HttpClient) { }

  /**
   * Obtener configuración global
   * GET /api/banco-plaza-instant-debit/config
   */
  getConfig = async (): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/config`;

    try {
      const response = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error obteniendo configuración:', error);
      throw error;
    }
  };

  getQueuedTransactions = async (
    page: number = 1,
    limit: number = 10,
    sort: number = -1
  ): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });

    const queryParams = `?page=${page}&limit=${limit}&sort=${sort}`;
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/queued-transactions${queryParams}`;

    try {
      const response = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error obteniendo transacciones encoladas:', error);
      throw error;
    }
  };

  searchTransactionsByNumber = async (
    query: string,
    dateInitial?: string,
    dateFinal?: string,
    state?: string,
    page: number = 1,
    limit: number = 10,
    sort: number = -1
  ): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    
    let queryParams = `?page=${page}&limit=${limit}&sort=${sort}`;
    
    if (query && query.trim() !== '') {
      queryParams += `&query=${encodeURIComponent(query.trim())}`;
    }
    
    if (dateInitial && dateInitial.trim() !== '') {
      queryParams += `&dateInitial=${encodeURIComponent(dateInitial.trim())}`;
    }
    
    if (dateFinal && dateFinal.trim() !== '') {
      queryParams += `&dateFinal=${encodeURIComponent(dateFinal.trim())}`;
    }
    
    if (state && state.trim() !== '') {
      queryParams += `&state=${encodeURIComponent(state.trim())}`;
    }
    
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/transactions/search${queryParams}`;

    try {
      const response = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error buscando transacciones por número:', error);
      throw error;
    }
  };

  /**
   * Verificación masiva de transacciones en cola (stuck)
   * POST /api/banco-plaza-instant-debit/verify-pending-payments
   * codePerson opcional (body)
   */
  verifyPendingPayments = async (codePerson?: string): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/verify-pending-payments`;

    const body: any = {};
    if (codePerson && codePerson.trim() !== '') {
      body.codePerson = codePerson.trim();
    }

    try {
      const response = await lastValueFrom(
        this.http.post(url, body, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error en verificación masiva:', error);
      throw error;
    }
  };

  /**
   * Actualizar configuración global
   * PUT /api/banco-plaza-instant-debit/config
   */
  updateConfig = async (config: { max_token_attempts: number; max_payment_retries: number; max_verification_attempts: number; verification_delay_seconds: number; max_cron_verification_attempts: number }): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/config`;

    try {
      const response = await lastValueFrom(
        this.http.put(url, config, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error actualizando configuración:', error);
      throw error;
    }
  };

  /**
   * Obtener transacciones por codePerson con filtros
   * GET /api/banco-plaza-instant-debit/transactions
   */
  getTransactionsByPerson = async (
    codePerson: string,
    dateInitial?: string,
    dateFinal?: string,
    state?: string,
    page: number = 1,
    limit: number = 10,
    sort: number = -1
  ): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    
    // Construir query params
    let queryParams = `?codePerson=${encodeURIComponent(codePerson)}`;
    
    if (dateInitial && dateInitial.trim() !== '') {
      queryParams += `&dateInitial=${encodeURIComponent(dateInitial.trim())}`;
    }
    
    if (dateFinal && dateFinal.trim() !== '') {
      queryParams += `&dateFinal=${encodeURIComponent(dateFinal.trim())}`;
    }
    
    if (state && state.trim() !== '') {
      queryParams += `&state=${encodeURIComponent(state.trim())}`;
    }
    
    queryParams += `&page=${page}&limit=${limit}&sort=${sort}`;
    
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/transactions${queryParams}`;

    try {
      const response = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error obteniendo transacciones:', error);
      throw error;
    }
  };

  /**
   * Obtener transacciones por cédula del pagador con filtros
   * GET /api/banco-plaza-instant-debit/transactions-by-payer
   */
  getTransactionsByPayerIdentity = async (
    identity: string,
    dateInitial?: string,
    dateFinal?: string,
    state?: string,
    page: number = 1,
    limit: number = 10,
    sort: number = -1
  ): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    
    // Construir query params
    let queryParams = `?identity=${encodeURIComponent(identity)}`;
    
    if (dateInitial && dateInitial.trim() !== '') {
      queryParams += `&dateInitial=${encodeURIComponent(dateInitial.trim())}`;
    }
    
    if (dateFinal && dateFinal.trim() !== '') {
      queryParams += `&dateFinal=${encodeURIComponent(dateFinal.trim())}`;
    }
    
    if (state && state.trim() !== '') {
      queryParams += `&state=${encodeURIComponent(state.trim())}`;
    }
    
    queryParams += `&page=${page}&limit=${limit}&sort=${sort}`;
    
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/transactions-by-payer${queryParams}`;

    try {
      const response = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error obteniendo transacciones por cédula del pagador:', error);
      throw error;
    }
  };

  /**
   * Obtener bitácoras de una transacción
   * GET /api/banco-plaza-instant-debit/transactions/:transactionNumber/bitacoras
   */
  getBitacorasByTransaction = async (transactionNumber: number): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/transactions/${transactionNumber}/bitacoras`;

    try {
      const response = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error obteniendo bitácoras:', error);
      throw error;
    }
  };

  /**
   * Verificar pago
   * POST /api/banco-plaza-instant-debit/verify-payment
   */
  verifyPayment = async (transactionNumber: number): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/verify-payment`;
    const body = { transactionNumber: transactionNumber };

    try {
      const response = await lastValueFrom(
        this.http.post(url, body, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error verificando pago:', error);
      throw error;
    }
  };

  getStuckTransactions = async (
    page: number = 1,
    limit: number = 10,
    codePerson?: string,
    sort: number = -1
  ): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });

    let queryParams = `?page=${page}&limit=${limit}&sort=${sort}`;

    if (codePerson && codePerson.trim() !== '') {
      queryParams += `&codePerson=${encodeURIComponent(codePerson.trim())}`;
    }

    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/stuck-transactions${queryParams}`;

    try {
      const response = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error obteniendo transacciones stuck:', error);
      throw error;
    }
  };

  getReallyStuckTransactions = async (
    page: number = 1,
    limit: number = 10,
    codePerson?: string,
    sort: number = -1
  ): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });

    let queryParams = `?page=${page}&limit=${limit}&sort=${sort}`;

    if (codePerson && codePerson.trim() !== '') {
      queryParams += `&codePerson=${encodeURIComponent(codePerson.trim())}`;
    }

    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/really-stuck-transactions${queryParams}`;

    try {
      const response = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error obteniendo transacciones atascadas:', error);
      throw error;
    }
  };

  /**
   * Obtener transacciones con anomalías
   * GET /api/banco-plaza-instant-debit/anomalies
   */
  getTransactionsWithAnomalies = async (
    page: number = 1,
    limit: number = 50,
    sort: number = -1
  ): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });

    const queryParams = `?page=${page}&limit=${limit}&sort=${sort}`;
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/anomalies${queryParams}`;

    try {
      const response = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error obteniendo transacciones con anomalías:', error);
      throw error;
    }
  };

  /**
   * Cancelar transacción (marca como FAILED)
   * POST /api/banco-plaza-instant-debit/cancel-payment
   */
  cancelPayment = async (transactionNumber: number): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/cancel-payment`;
    const body = { transactionNumber: transactionNumber };

    try {
      const response = await lastValueFrom(
        this.http.post(url, body, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error cancelando transacción:', error);
      throw error;
    }
  };

  /**
   * Liberar transacción de la cola de verificación
   * POST /api/banco-plaza-instant-debit/release-verification
   */
  releaseVerificationQueue = async (transactionNumber: number, createPayment: boolean = false): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/release-verification`;
    const body = { transactionNumber, createPayment };

    try {
      const response = await lastValueFrom(
        this.http.post(url, body, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error liberando transacción de la cola:', error);
      throw error;
    }
  };

  /**
   * Cancelar transacción (marca como CANCELED)
   * POST /api/banco-plaza-instant-debit/cancel-transaction
   */
  cancelTransaction = async (transactionNumber: number): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/cancel-transaction`;
    const body = { transactionNumber: transactionNumber };

    try {
      const response = await lastValueFrom(
        this.http.post(url, body, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error cancelando transacción (CANCELED):', error);
      throw error;
    }
  };

  /**
   * Completar manualmente transacción con timeout
   * POST /api/banco-plaza-instant-debit/complete-timeout-transaction
   */
  completeTimeoutTransaction = async (transactionNumber: number): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/complete-timeout-transaction`;
    const body = { transactionNumber: transactionNumber };

    try {
      const response = await lastValueFrom(
        this.http.post(url, body, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error completando transacción con timeout:', error);
      throw error;
    }
  };

  /**
   * Sincronizar estados de transacciones MANUAL_COMPLETE
   * POST /api/banco-plaza-instant-debit/sync-manual-complete
   */
  syncManualCompleteTransactions = async (): Promise<any> => {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    const url = `${this.baseUrl}/api/banco-plaza-instant-debit/sync-manual-complete`;

    try {
      const response = await lastValueFrom(
        this.http.post(url, {}, { headers })
      );
      return response;
    } catch (error) {
      console.error('Error sincronizando transacciones MANUAL_COMPLETE:', error);
      throw error;
    }
  };
}
